/**
 * Review state for a person card.
 *
 * Two visible states on the User List: "Check report" (nothing is asking for
 * review) and "Needs review". Both open the same Mobility Metrics destination;
 * the state only changes the label and treatment.
 *
 * Deliberately an aggregate rather than a single test, so further triggers can
 * be added by appending one line to `REVIEW_TRIGGERS`. It is not a rules
 * engine and should not become one.
 */

/** A Mobility Report is current for 30 days; day 31 is the first stale day. */
export const MOBILITY_REPORT_VALID_DAYS = 30;

/**
 * Whole calendar days between two dates, normalized to local midnight.
 *
 * Normalizing matters: comparing raw timestamps would flip a person's state
 * partway through the day depending on the hour the report was filed.
 */
export function calendarDaysBetween(from, to) {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
}

/**
 * Report date -> is it stale?
 *
 *   report date = day 1   -> 0 days elapsed  -> current
 *   day 30                -> 29 days elapsed -> current
 *   day 31                -> 30 days elapsed -> STALE
 *
 * so the test is `elapsed >= MOBILITY_REPORT_VALID_DAYS`.
 *
 * Returns `null` when there is no usable date, which is NOT the same as false:
 * callers must decide what an unknown report means rather than treating it as
 * current. See `mobilityReportDateOf`.
 */
export function isMobilityReportStale(reportDate, today = new Date()) {
  if (!reportDate) return null;
  const elapsed = calendarDaysBetween(reportDate, today);
  if (elapsed === null) return null;
  return elapsed >= MOBILITY_REPORT_VALID_DAYS;
}

/**
 * Read the last Mobility Report date off a person from the list response.
 *
 * `GET /v3/seniors` returns `last_mobility_report_at` (ISO date, or null when
 * the person has no report yet). The other names are kept as fallbacks in case
 * the field is surfaced under a different key. A null/absent value means "no
 * report on record", which `getReviewState` treats as "needs review".
 */
export function mobilityReportDateOf(person) {
  if (!person) return null;
  return (
    person.last_mobility_report_at ??
    person.last_mobility_report_date ??
    person.lastMobilityReportAt ??
    null
  );
}

/**
 * Review triggers, aggregated. Add a trigger by adding an entry.
 *
 * `isIncompleteProfile` is deliberately NOT a trigger here: it already has its
 * own "Complete the Profile" affordance on the card, and folding it in would
 * change existing behaviour and turn a profile-completeness flag into a
 * clinical review signal.
 */
const REVIEW_TRIGGERS = [
  {
    // No mobility report on record yet — the person has not been assessed, so
    // they need a review. A missing date is treated as "needs review", not as
    // "up to date".
    reason: "mobility_report_missing",
    test: (person) => mobilityReportDateOf(person) == null,
  },
  {
    reason: "mobility_report_stale",
    test: (person, today) =>
      isMobilityReportStale(mobilityReportDateOf(person), today) === true,
  },
];

/**
 * Aggregate review state for one person.
 *
 * `needsReview` is true when any trigger fires. `reasons` lists which ones, so
 * a future caller can explain the state without re-deriving it.
 */
export function getReviewState(person, today = new Date()) {
  const reasons = REVIEW_TRIGGERS.filter((trigger) =>
    trigger.test(person, today),
  ).map((trigger) => trigger.reason);
  const backendStatus = String(person?.pharmacy_dap_status || "")
    .trim()
    .toLowerCase();
  const knownBackendStatus = [
    "needs_review",
    "check_report",
    "reviewed",
  ].includes(backendStatus)
    ? backendStatus
    : null;
  const latestAssessmentId =
    person?.last_mobility_report_id ?? person?.lastMobilityReportId ?? null;
  const reviewedAssessmentId =
    person?.last_pharmacy_dap_review_assessment_id ??
    person?.lastPharmacyDapReviewAssessmentId ??
    null;
  const inferredStatus = reasons.length
    ? "needs_review"
    : latestAssessmentId && reviewedAssessmentId === latestAssessmentId
      ? "reviewed"
      : "check_report";
  const status = knownBackendStatus || inferredStatus;
  return { needsReview: status === "needs_review", status, reasons };
}

export const hasNeedsReview = (person, today = new Date()) =>
  getReviewState(person, today).needsReview;
